import { useEffect, useId, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AlertCircle } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { createPasskeyClient, passkeyError, type PasskeyStatus } from '../auth/passkeys'

export function PasskeySettings({
  apiUrl,
  token,
  admin = false,
  targetUserId,
}: {
  apiUrl: string
  token: string
  admin?: boolean
  targetUserId?: string
}) {
  const client = useMemo(() => createPasskeyClient(apiUrl, admin), [apiUrl, admin])
  const id = useId()
  const [enabled, setEnabled] = useState(false)
  const [status, setStatus] = useState<PasskeyStatus>()
  const [password, setPassword] = useState('')
  const [secret, setSecret] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [showForm, setShowForm] = useState(false)
  useEffect(() => {
    let active = true
    void client
      .enabled()
      .then(async (enabled) => {
        const status = enabled && !targetUserId ? await client.status(token) : undefined
        if (active) {
          setEnabled(enabled)
          setStatus(status)
        }
      })
      .catch(() => {
        if (active)
          setMessage('Passkey settings could not be loaded. Your authenticator code still works.')
      })
    return () => {
      active = false
    }
  }, [client, token, targetUserId])

  async function run(action: 'register' | 'retry' | 'cancel' | 'revoke' | 'reopen') {
    setBusy(true)
    setMessage('')
    try {
      const proof = { password, secret }
      if ((action === 'revoke' || action === 'reopen') && targetUserId) {
        await client.recover(token, proof, targetUserId, action, reference)
        toast.success(
          action === 'revoke'
            ? 'Passkeys revoked and existing sessions invalidated. Enrollment remains locked.'
            : 'Enrollment reopened. The user must authenticate with their password and authenticator code.',
        )
      } else if (action === 'register' || action === 'retry' || action === 'cancel') {
        await client[action](token, proof)
        toast.success(
          action === 'cancel'
            ? 'Pending enrollment cancelled. Your authenticator code still works.'
            : 'Passkey verified and enrollment confirmed',
        )
        setShowForm(false)
      }
    } catch (error) {
      setMessage(passkeyError(error))
    } finally {
      setPassword('')
      setSecret('')
      setBusy(false)
      if (!targetUserId) {
        try {
          setStatus(await client.status(token))
        } catch {
          setMessage('Sign in again to check enrollment status.')
        }
      }
    }
  }

  const errorAlert = message ? (
    <div
      role="alert"
      className="flex gap-2 rounded-md border border-destructive bg-destructive/5 p-3 text-sm text-red-700 dark:text-red-300"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p>{message}</p>
    </div>
  ) : null
  if (!enabled) return errorAlert
  const pending = status?.passkeys.some((p) => !p.confirmed)
  const locked = status?.locked
  const confirmedCount = status?.passkeys.filter((p) => p.confirmed && p.supported).length ?? 0
  if (!targetUserId && locked && confirmedCount > 0 && !pending) return errorAlert
  return (
    <section
      className="mt-6 rounded-xl border border-border p-4 space-y-3"
      aria-label={targetUserId ? 'Passkey recovery' : 'Passkeys'}
    >
      <h3 className="font-semibold">
        {targetUserId ? 'Administrator-assisted passkey recovery' : 'Passkeys for login MFA'}
      </h3>
      {targetUserId ? (
        <p className="text-sm">
          Verify the account holder’s identity through your established process first. Record why
          recovery is needed, never identity documents or secrets. Revocation invalidates existing
          sessions and locks enrollment. Reopen enrollment separately after verification and any
          password/TOTP recovery.
        </p>
      ) : (
        <>
          {!locked && confirmedCount === 0 && (
            <>
              <p className="text-sm">
                Sign in with your password and a passkey instead of an authenticator code.
              </p>
              <p className="text-sm">
                This application supports passkeys stored in Apple Passwords (iCloud Keychain) or
                Google Password Manager. Once a passkey is added, traders need to contact an
                administrator to change their passkey.
              </p>
              <p className="text-sm">
                Your device will ask you to save a passkey, then verify it once to finish setup.
              </p>
            </>
          )}
          {(confirmedCount > 0 || locked || pending) && (
            <p className="text-sm">
              {confirmedCount > 0 &&
                `${confirmedCount} ${confirmedCount === 1 ? 'passkey' : 'passkeys'} ready for login. `}
              {locked
                ? 'Contact your administrator to change your passkey. You can still sign in with an authenticator code.'
                : pending
                  ? 'Enrollment pending: verify the saved passkey within five minutes, or cancel and start again. It cannot be used for login yet.'
                  : 'You can also sign in with an authenticator code.'}
            </p>
          )}
          {status?.passkeys.some((p) => !p.supported) && (
            <p className="text-sm">
              Previously registered or unsupported passkeys need to be set up again with Apple
              Passwords or Google Password Manager. Your authenticator code still works.
            </p>
          )}
        </>
      )}
      {(!locked || targetUserId) && (
        <>
          {!showForm ? (
            <Button onClick={() => setShowForm(true)}>
              {targetUserId
                ? 'Recover passkeys'
                : pending
                  ? 'Complete enrollment'
                  : 'Add a passkey'}
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">Confirm with your current password and authenticator code.</p>
              <Label htmlFor={`${id}-password`}>Current password</Label>
              <Input
                id={`${id}-password`}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <Label htmlFor={`${id}-code`}>Authenticator code</Label>
              <Input
                id={`${id}-code`}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                disabled={busy}
              />
              {targetUserId && (
                <>
                  <Label htmlFor={`${id}-case`}>Reason for passkey recovery</Label>
                  <Input
                    id={`${id}-case`}
                    value={reference}
                    maxLength={200}
                    onChange={(e) => setReference(e.target.value)}
                    disabled={busy}
                  />
                </>
              )}
              <div className="flex flex-wrap gap-2">
                {targetUserId ? (
                  <>
                    <Button
                      disabled={
                        busy || !password || secret.length !== 6 || reference.trim().length < 8
                      }
                      onClick={() => void run('revoke')}
                    >
                      Revoke credentials and sessions
                    </Button>
                    <Button
                      disabled={
                        busy || !password || secret.length !== 6 || reference.trim().length < 8
                      }
                      onClick={() => void run('reopen')}
                    >
                      Reopen enrollment after verification
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      disabled={busy || !password || secret.length !== 6 || !status}
                      onClick={() => void run(pending ? 'retry' : 'register')}
                    >
                      {busy
                        ? 'Waiting for your device…'
                        : pending
                          ? 'Verify pending passkey'
                          : 'Add and verify a passkey'}
                    </Button>
                    {pending && (
                      <Button
                        disabled={busy || !password || secret.length !== 6}
                        onClick={() => void run('cancel')}
                      >
                        Cancel pending enrollment
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {errorAlert}
    </section>
  )
}
